import { MockConnector, FullEnrollmentConnector, FasttrackConnector } from '.';
import AbstractConnector from './abstract-connector';
import { EnrollmentContextService } from '../enrollment/enrollment-context';
import { Person, ServiceEnrollment } from '../enrollment/types';
import { ServiceStatus } from '../../constants/service.constants';
import StaticConnector from './static-connector';
import { CardData } from '../../../domains/confirmation/components/services/card';
import { Configuration } from '../../../../hooks/useConfiguration';

export enum requestStatus {
	INACTIVE = 'inactive',
	INPROGRESS = 'progress',
	COMPLETED = 'success',
	TIMEOUT = 'timeout',
	ERROR = 'error',
}

export type RequestBody = {
	patient?: Person;
	performer?: { type: string };
	consents?: { name: string; dateTime: string }[];
	serviceEnrollments: ServiceEnrollment[];
	product: string;
};

export type ResponseData = {
	service: string;
	state: string;
	card?: CardData;
	patientId?: string;
};

export type EnrollmentResponse = {
	status: requestStatus;
	data: ResponseData[];
	time?: number;
	patientId?: string;
};

export default class APIConnector {
	private readonly strategy: AbstractConnector;
	private readonly config: Configuration;

	constructor(
		config: Configuration,
		enrollmentCtxt: EnrollmentContextService
	) {
		this.config = config;
		if (config.mock) {
			this.strategy = new MockConnector(config.mock.connector);
		} else if (enrollmentCtxt.isStatic(config.product)) {
			this.strategy = new StaticConnector(config);
		} else {
			this.strategy = enrollmentCtxt.isFastTrack(config.product)
				? new FasttrackConnector()
				: new FullEnrollmentConnector();
		}
	}

	public async enroll(
		input: RequestBody,
		callback: (a: EnrollmentResponse) => void
	): Promise<void> {
		return this.strategy.enroll(input, (enrollmentData: EnrollmentResponse) => {
			enrollmentData.data = this.addStaticData(input, enrollmentData.data);
			callback(enrollmentData);
		});
	}

	private addStaticData(
		input: RequestBody,
		data: ResponseData[]
	): ResponseData[] {
		return Array.from(
			new Map(
				data
					.concat(
						getStaticServiceData(this.config, input.serviceEnrollments)
					)
					.map((item) => [item.service, item])
			).values()
		);
	}
}

export function getStaticServiceData(
	productConfig: Configuration,
	servicesRequested: ServiceEnrollment[]
): ResponseData[] {
	const retrievedServiceData: ResponseData[] = [];
	servicesRequested.forEach((service) => {
		const serviceConfig = productConfig.product.getService(service.name);
		if (serviceConfig?.static) {
			retrievedServiceData.push({
				service: service.name,
				card: serviceConfig.static as CardData,
				state: ServiceStatus.enrolled,
			});
		}
	});
	return retrievedServiceData;
}
